import React, { type JSX } from 'react';
import { Stack, Typography, Paper, Switch, Container, Button, FormControl, Select, MenuItem } from '@mui/material';

import Remove from '@mui/icons-material/Remove';
import Add from '@mui/icons-material/Add';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setDisplayPreferences } from '@fiji/common/src/features/widgetManagement/detailsWidgetSlice';
import { useGetChannelValues, useWidgetConfigHandlers } from './hooks';
import { useTheme } from '@mui/material/styles';
import { CustomTransComponent } from 'components';
import { useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export const DisplayPreferences = (props: any): JSX.Element => {
    const theme: any = useTheme();
    const primaryPalette = theme.palette.primary;

    const preferencesPayload = useTypedSelector((state) => state.details);
    const {
        showWidgetTitleBar = false,
        isTotalCountVisible = true,
        showLastUpdateDate = false,
        countMode,
        secondaryChannelCount,
        viewAllButton = false,
    } = preferencesPayload ?? {};
    const isWidgetHeaderVisible = Boolean(showWidgetTitleBar);
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canViewProperties = hasPermission('device-properties_view-devices');
    const canViewSettings = hasPermission('device-settings_view-devices');
    const canViewDetails = hasPermission('device-details_view-devices');

    const getIsVisable = (): boolean => {
        if (props?.widgetData?.widgetTypeId === 'properties') {
            return canViewProperties;
        } else if (props?.widgetData?.widgetTypeId === 'settings') {
            return canViewSettings;
        }
        return canViewDetails;
    };
    const dispatch = useAppDispatch();
    const {
        details: { widgetDetails },
    } = useWidgetConfigHandlers();
    const widgetTypeId = widgetDetails?.widgetType?.id;
    const isSettingsOrPropertiesWidget = widgetTypeId === 'settings' || widgetTypeId === 'properties';
    const rawMaxSecondaryChannelCount = Number(
        widgetDetails?.config?.secondaryChannelCount ?? props?.widgetData?.secondaryChannelCount
    );
    const maxSecondaryChannelCount = Number.isFinite(rawMaxSecondaryChannelCount)
        ? Math.max(rawMaxSecondaryChannelCount, 1)
        : 30;
    const rawSecondaryChannelCount = Number(secondaryChannelCount);
    const normalizedSecondaryChannelCount = Number.isFinite(rawSecondaryChannelCount)
        ? Math.max(rawSecondaryChannelCount, 1)
        : 1;
    const effectiveSecondaryChannelCount = Math.min(normalizedSecondaryChannelCount, maxSecondaryChannelCount);

    const toggleSx = {
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: primaryPalette.main,
            '& + .MuiSwitch-track': {
                backgroundColor: primaryPalette.main,
            },
        },
        '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
            color: theme.palette.primary[50],
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary[200],
            },
        },
    };

    const dependentToggleSx = {
        opacity: isWidgetHeaderVisible ? 1 : 0.4,
        ...toggleSx,
    };

    React.useEffect(() => {
        if ((secondaryChannelCount ?? 0) > maxSecondaryChannelCount) {
            dispatch(
                setDisplayPreferences({
                    key: 'secondaryChannelCount',
                    value: maxSecondaryChannelCount,
                })
            );
        }
    }, [dispatch, secondaryChannelCount, maxSecondaryChannelCount]);

    useGetChannelValues(isSettingsOrPropertiesWidget ? 'details' : widgetTypeId);

    return (
        <Paper className="padding-16">
            <Container className="bg-content-wrapper min-height-828 padding-16">
                <Stack className="bg-white">
                    <Stack
                        direction={'row'}
                        p={2}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        className="border-bottom-1"
                    >
                        <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'}>
                            <CustomTransComponent
                                translationKey={'WIDGETS:GROUP_LIST.DISPLAY_PREFERENCE.DISPLAY_WIDGET_HEADER'}
                            />
                        </Typography>
                        <Switch
                            sx={toggleSx}
                            checked={isWidgetHeaderVisible}
                            onChange={(): void => {
                                dispatch(
                                    setDisplayPreferences({
                                        key: 'showWidgetTitleBar',
                                        value: !showWidgetTitleBar,
                                    })
                                );
                            }}
                        />
                    </Stack>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        p={2}
                        className="border-bottom-1"
                    >
                        <Typography
                            variant="subtitle2"
                            fontSize={'14px'}
                            fontWeight={'600'}
                            sx={{ opacity: isWidgetHeaderVisible ? 1 : 0.4, pl: 2 }}
                        >
                            {isSettingsOrPropertiesWidget ? (
                                <CustomTransComponent
                                    translationKey={'WIDGETS:GROUP_LIST.DISPLAY_PREFERENCE.DISPLAY_TOTAL_COUNT'}
                                />
                            ) : (
                                <CustomTransComponent
                                    translationKey={'WIDGETS:COMMON_PREFERENCES.TOTAL_COUNT_TEXT'}
                                    replace={{ countKey: props?.countText ?? 'count' }}
                                />
                            )}
                        </Typography>
                        <Switch
                            disabled={!isWidgetHeaderVisible}
                            sx={dependentToggleSx}
                            checked={isTotalCountVisible}
                            onChange={(): void => {
                                dispatch(
                                    setDisplayPreferences({
                                        key: 'isTotalCountVisible',
                                        value: !isTotalCountVisible,
                                    })
                                );
                            }}
                        />
                    </Stack>
                    <Stack
                        direction={'row'}
                        p={2}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        className="border-bottom-1"
                    >
                        <Typography
                            variant="subtitle2"
                            fontSize={'14px'}
                            fontWeight={'600'}
                            sx={{ opacity: isWidgetHeaderVisible ? 1 : 0.4, pl: 2 }}
                        >
                            <CustomTransComponent
                                translationKey={'WIDGETS:GROUP_LIST.DISPLAY_PREFERENCE.DISPLAY_LAST_UPDATED'}
                            />
                        </Typography>
                        <Switch
                            disabled={!isWidgetHeaderVisible}
                            sx={dependentToggleSx}
                            checked={showLastUpdateDate}
                            onChange={(): void => {
                                dispatch(
                                    setDisplayPreferences({
                                        key: 'showLastUpdateDate',
                                        value: !showLastUpdateDate,
                                    })
                                );
                            }}
                        />
                    </Stack>
                    {props?.countText && (
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            py={1}
                            px={2}
                            className="border-bottom-1"
                        >
                            <Typography variant="subtitle2" fontWeight={'600'}>
                                <CustomTransComponent
                                    translationKey={'WIDGETS:COMMON_PREFERENCES.AMOUNT_OF_CHANNELS_DISPLAY_TEXT'}
                                    replace={{ channelType: props?.countText }}
                                />
                            </Typography>
                            <FormControl className="min-width-250 ">
                                <Select
                                    className="custom-select"
                                    labelId="demo-simple-select-helper-label"
                                    id="demo-simple-select-helper"
                                    variant="filled"
                                    defaultValue={countMode}
                                    onChange={(e: any): void => {
                                        const nextCountMode = e.target.value;
                                        const nextSecondaryChannelCount =
                                            nextCountMode === 'all' ? maxSecondaryChannelCount : 6;
                                        dispatch(setDisplayPreferences({ key: 'countMode', value: e.target.value }));
                                        dispatch(
                                            setDisplayPreferences({
                                                key: 'secondaryChannelCount',
                                                value: nextSecondaryChannelCount,
                                            })
                                        );
                                    }}
                                >
                                    <MenuItem value={'all'}>
                                        <CustomTransComponent translationKey={'SCHEDULING:ALL'} />
                                    </MenuItem>
                                    <MenuItem value={'custom'}>
                                        <CustomTransComponent translationKey={'COMMON:CUSTOM'} />
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    )}

                    {(countMode === 'custom' || widgetTypeId === 'details') && (
                        <Stack
                            direction={'row'}
                            p={2}
                            className="border-bottom-1"
                            justifyContent={'space-between'}
                            alignItems={'center'}
                        >
                            <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'} className="black-700">
                                <CustomTransComponent
                                    translationKey={'WIDGETS:COMMON_PREFERENCES.MAXIMUM_CHANNEL_TEXT'}
                                    replace={{ channelType: props?.countText ?? 'secondary channels' }}
                                />
                            </Typography>
                            <Stack direction={'row'} alignItems={'center'}>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    className="height-46 width-46 bg-content-wrapper"
                                    disabled={effectiveSecondaryChannelCount === 1}
                                    onClick={(): void => {
                                        dispatch(
                                            setDisplayPreferences({
                                                key: 'secondaryChannelCount',
                                                value: effectiveSecondaryChannelCount - 1,
                                            })
                                        );
                                    }}
                                >
                                    <Remove className="content-color" />
                                </Button>
                                <Typography px={3} variant="body1" fontWeight={'600'} textAlign={'center'}>
                                    {effectiveSecondaryChannelCount}
                                </Typography>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    className="height-46 width-46 bg-content-wrapper"
                                    disabled={effectiveSecondaryChannelCount === maxSecondaryChannelCount}
                                    onClick={(): void => {
                                        dispatch(
                                            setDisplayPreferences({
                                                key: 'secondaryChannelCount',
                                                value: effectiveSecondaryChannelCount + 1,
                                            })
                                        );
                                    }}
                                >
                                    <Add className="content-color" />
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                    {getIsVisable() && (
                        <>
                            <Stack
                                direction={'row'}
                                p={2}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                className="border-bottom-1"
                            >
                                <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'}>
                                    <CustomTransComponent
                                        translationKey={'WIDGETS:GROUP_LIST.DISPLAY_PREFERENCE.DISPLAY_VIEW_ALL'}
                                    />
                                </Typography>
                                <Switch
                                    sx={toggleSx}
                                    checked={viewAllButton}
                                    onChange={(): void => {
                                        dispatch(
                                            setDisplayPreferences({
                                                key: 'viewAllButton',
                                                value: !viewAllButton,
                                            })
                                        );
                                    }}
                                />
                            </Stack>
                        </>
                    )}
                </Stack>
            </Container>
        </Paper>
    );
};
